import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="glass-card p-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-gray-600 mb-8">Last Updated: 12.03.2025</p>

          <div className="prose prose-gray prose-lg max-w-none space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">DECIDE ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Information You Provide Directly</h3>
            <p>When you use DECIDE, you provide:</p>
            <ul>
              <li><strong>Account Information:</strong> Email address, name, password (encrypted)</li>
              <li><strong>Decision Content:</strong> The text of your decisions, including thoughts about Logic, Universe, and Vibe factors</li>
              <li><strong>Decision Records:</strong> Your decision choices (YES/WAIT/NO) and outcomes</li>
              <li><strong>Feedback:</strong> Responses about whether our analysis was helpful</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed">We automatically collect:</p>
            <ul>
              <li><strong>Usage Data:</strong> How you interact with the App, features used, time spent</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
              <li><strong>Technical Data:</strong> Error logs, performance data, access times</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Primary Uses</h3>
            <p className="text-gray-700 leading-relaxed">We use your information to:</p>
            <ul>
              <li><strong>Provide the Service:</strong> Process your decisions and generate recommendations</li>
              <li><strong>Improve Recommendations:</strong> Refine our AI algorithms to provide better analysis</li>
              <li><strong>Personalize Experience:</strong> Learn your decision-making patterns over time</li>
              <li><strong>Maintain Security:</strong> Protect against fraud, abuse, and security threats</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Training Our AI System</h3>
            <p><strong>IMPORTANT:</strong> We may use anonymized and aggregated decision data to train and improve our AI recommendation system. This means:</p>
            <ul>
              <li><strong>What we use:</strong> The patterns, themes, and decision structures from user inputs</li>
              <li><strong>How we protect you:</strong> All data used for training is anonymized (stripped of identifying information)</li>
              <li><strong>Why it matters:</strong> This helps DECIDE provide better recommendations to all users</li>
              <li><strong>Your control:</strong> You can opt out of having your data used for training by contacting us</li>
            </ul>

            <p><strong>We will never:</strong></p>
            <ul>
              <li>Share your specific decision details publicly</li>
              <li>Use your personal information in training data</li>
              <li>Allow other users to see your decisions</li>
              <li>Sell your decision content to third parties</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. How We Share Your Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 We Do Not Sell Your Data</h3>
            <p className="text-gray-700 leading-relaxed">We do not sell, rent, or trade your personal information to third parties for marketing purposes.</p>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Limited Sharing</h3>
            <p className="text-gray-700 leading-relaxed">We may share your information only in these circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> With trusted third-party vendors who help us operate the App (hosting, analytics, payment processing)</li>
              <li><strong>Legal Requirements:</strong> If required by law, court order, or government request</li>
              <li><strong>With Your Consent:</strong> When you explicitly consent</li>
              <li><strong>Anonymized Data:</strong> We may share aggregated, anonymized data that cannot identify you</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">We implement appropriate technical and organizational measures to protect your data:</p>
            <ul>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Encryption of sensitive data at rest</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
            </ul>
            <p><strong>Note:</strong> While we strive to protect your information, no method of transmission or storage is 100% secure.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Data Retention</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.1 How Long We Keep Your Data</h3>
            <ul>
              <li><strong>Active Accounts:</strong> We retain your data for as long as your account is active</li>
              <li><strong>Deleted Accounts:</strong> When you delete your account, we permanently delete your personal data and decision content immediately</li>
              <li><strong>Anonymized Training Data:</strong> Data that has been fully anonymized and aggregated for AI training may be retained</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed">Depending on your location, you may have these rights:</p>
            <ul>
              <li><strong>Access and Portability:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Opt-Out Rights:</strong> Opt out of data collection for AI training or marketing communications</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. How to Exercise Your Rights</h2>
            <p className="text-gray-700 leading-relaxed">To exercise any of these rights, contact us at: <a href="mailto:decide@elev8tek.com" className="text-purple-600 hover:text-purple-700">decide@elev8tek.com</a></p>
            <p className="text-gray-700 leading-relaxed">We will respond within 30 days.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">DECIDE is not intended for users under 18 years of age. We do not knowingly collect information from children.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. California Privacy Rights (CCPA)</h2>
            <p className="text-gray-700 leading-relaxed">If you are a California resident, you have additional rights under the California Consumer Privacy Act, including the right to know what personal information is collected and the right to deletion.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. European Privacy Rights (GDPR)</h2>
            <p className="text-gray-700 leading-relaxed">If you are in the European Economic Area, you have rights under GDPR, including the right to access, correct, or delete your data and the right to data portability.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed">We use cookies and similar technologies to remember your preferences, understand how you use the App, and improve performance. You can control cookies through your browser settings.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">We may update this Privacy Policy from time to time. We will notify you of material changes via email or App notification. Continued use after changes constitutes acceptance.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">For questions, concerns, or requests regarding this Privacy Policy:</p>
            <p><strong>Email:</strong> <a href="mailto:decide@elev8tek.com" className="text-purple-600 hover:text-purple-700">decide@elev8tek.com</a></p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">15. Consent</h2>
            <p className="text-gray-700 leading-relaxed">By using DECIDE, you consent to:</p>
            <ul>
              <li>Collection and use of your information as described</li>
              <li>Use of anonymized data for AI training purposes</li>
              <li>Transfer of data as described in this policy</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
